import { useEffect, useRef, useState } from 'react';
import { useStore } from '../state/store';

export function TranscriptPanel() {
  const { events, ingestLine, ingestBlock } = useStore((s) => ({
    events: s.events,
    ingestLine: s.ingestLine,
    ingestBlock: s.ingestBlock,
  }));
  const [text, setText] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const onShortcut = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        taRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, []);

  async function submit() {
    const t = text.trim();
    if (!t) return;
    if (t.includes('\n')) await ingestBlock(t);
    else await ingestLine(t);
    setText('');
    // Keep focus in the textarea so the next entry can be typed immediately.
    taRef.current?.focus();
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
      return;
    }
    // The box autofocuses, which used to swallow the Space shortcut and
    // type a space instead of starting dictation. A space in an EMPTY
    // box can only mean "toggle recording" — descriptions never start
    // with a space.
    if ((e.key === ' ' || e.code === 'Space') && text.trim() === '') {
      const toggle = useStore.getState().requestMicToggle;
      if (toggle) {
        e.preventDefault();
        toggle();
      }
    }
  }

  return (
    <div className="transcript">
      <div className="transcript-input">
        <textarea
          ref={taRef}
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder='Type or paste a line. End with a time, e.g. "review Defence two units". Enter to submit. Shift+Enter for newline.'
          autoFocus
        />
        <button onClick={() => void submit()}>Submit</button>
      </div>
      <div className="transcript-history">
        {[...events].slice(-200).reverse().map((e) => (
          <div key={e.id} className={`evt evt--${e.eventType}`}>
            <span className="evt-type">{e.eventType}</span>
            <span className="evt-text">{e.rawText || e.normalisedText || '(manual)'}</span>
            {e.newValue && <span className="evt-new">{e.newValue}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
